import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

const PRESET_COLORS = [
  "#F59E0B", // Amber
  "#BE123C", // Rose
  "#3B82F6", // Blue
  "#10B981", // Emerald
  "#8B5CF6", // Violet
  "#EC4899", // Pink
  "#F97316", // Orange
  "#06B6D4", // Cyan
];

export const CategoryManager = ({ isOpen, onClose, categories, onCategoriesChange }) => {
  const [newCategoryName, setNewCategoryName] = useState("");
  const [selectedColor, setSelectedColor] = useState(PRESET_COLORS[0]);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;

    setIsCreating(true);
    try {
      const response = await fetch(`${API}/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newCategoryName.trim(),
          color: selectedColor
        })
      });

      if (response.ok) {
        toast.success('Category created!');
        setNewCategoryName("");
        setSelectedColor(PRESET_COLORS[0]);
        onCategoriesChange();
      } else {
        toast.error('Failed to create category');
      }
    } catch (error) {
      toast.error('Failed to create category');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName, isDefault) => {
    if (isDefault) {
      toast.error('Cannot delete default categories');
      return;
    }

    try {
      const response = await fetch(`${API}/categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`"${categoryName}" deleted`);
        onCategoriesChange();
      } else {
        toast.error('Failed to delete category');
      }
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-display text-xl uppercase tracking-wider">
            Manage Categories
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            Create custom categories to organize your jokes by theme or style.
          </DialogDescription>
        </DialogHeader>

        {/* Create New Category */}
        <form onSubmit={handleCreateCategory} className="space-y-4 pb-4 border-b border-border">
          <div className="space-y-2">
            <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              New Category
            </Label>
            <Input
              placeholder="Category name..."
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="bg-background/50 border-border focus:border-primary"
              data-testid="new-category-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Color
            </Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  className={`w-8 h-8 rounded-sm transition-transform ${selectedColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-card scale-110' : 'hover:scale-105'}`}
                  style={{ backgroundColor: color }}
                  data-testid={`color-${color}`}
                />
              ))}
            </div>
          </div>

          <Button 
            type="submit"
            disabled={!newCategoryName.trim() || isCreating}
            className="w-full bg-primary text-primary-foreground font-semibold uppercase tracking-wider hover:bg-spotlight-hover"
            data-testid="create-category-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Category
          </Button>
        </form>

        {/* Existing Categories */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Your Categories
          </Label>
          <ScrollArea className="h-64">
            <div className="space-y-2 pr-4">
              {categories.map((cat) => (
                <div 
                  key={cat.category_id}
                  className="flex items-center justify-between p-3 bg-background/50 rounded-sm border border-border"
                  data-testid={`category-item-${cat.category_id}`}
                >
                  <div className="flex items-center gap-3">
                    <span 
                      className="w-4 h-4 rounded-sm" 
                      style={{ backgroundColor: cat.color }}
                    />
                    <span className="font-medium">{cat.name}</span>
                    {cat.is_default && (
                      <span className="text-xs text-muted-foreground">(default)</span>
                    )}
                  </div>
                  {!cat.is_default && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDeleteCategory(cat.category_id, cat.name, cat.is_default)}
                      data-testid={`delete-category-${cat.category_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Close Button */}
        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={onClose} data-testid="close-category-manager-btn">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryManager;
